/**
 * RD product gallery magnifier.
 * Delegated events survive variant AJAX replacement and Theme Editor reloads.
 * High-resolution images are requested on interaction, not on initial page load.
 */
(function () {
  'use strict';
  if (window.RDProductMagnifier) return;
  window.RDProductMagnifier = true;

  var SECTION = '[data-section-type="product"][data-rd-product-zoom]';
  var MEDIA = '.js-product-media-container, .product-media, .product-media-new';
  var IMAGE = '.product-media__image, .product-media-new__image';
  var FRAME = '.js-product-media-item-variant, .product-media__slider-slide-wrapper, .product-media-new__slider-slide-wrapper, .js-product-gallery-slide';
  var THUMB = '.product-media__slider-thumbnails, .product-media-new__slider-thumbnails, .product-media__slider-thumbnails-stacked, .product-media__slider-thumbnails-slide, .js-product-media-thumbnails-slide, [data-thumbnail]';
  var desktopQuery = window.matchMedia('(min-width: 769px) and (hover: hover) and (pointer: fine)');
  var active = null;
  var gesture = null;
  var animationFrame = 0;
  var pendingPoint = null;
  var eventBusBound = false;
  var variantEvents = {};
  var domObserver = new MutationObserver(function () {
    if (active && (!active.image.isConnected || !active.frame.isConnected)) deactivate();
  });

  function desktopEnabled(section) {
    return desktopQuery.matches && section.classList.contains('product--rd-image-zoom-desktop');
  }

  function touchEnabled(section) {
    return !desktopQuery.matches && section.classList.contains('product--rd-image-zoom-mobile');
  }

  function context(target) {
    if (!target || !target.closest) return null;
    var image = target.matches(IMAGE) ? target : target.closest(IMAGE);
    if (!image || image.closest(THUMB)) return null;
    var section = image.closest(SECTION);
    if (!section || !image.closest(MEDIA)) return null;
    var slide = image.closest('.js-product-gallery-slide, .swiper-slide');
    if (slide && (slide.hasAttribute('data-model-slide') || slide.querySelector('video, iframe, model-viewer, .js-video-wrapper, .js-product-media-model-content'))) return null;
    return { section: section, image: image };
  }

  function numberVariable(style, name, fallback) {
    var value = parseFloat(style.getPropertyValue(name));
    return Number.isFinite(value) ? value : fallback;
  }

  function largestSource(srcset) {
    var best = { score: 0, url: '' };
    (srcset || '').split(',').forEach(function (candidate, index) {
      var parts = candidate.trim().split(/\s+/);
      var score = parts[1] ? parseFloat(parts[1]) : index + 1;
      if (parts[0] && score > best.score) best = { score: score, url: parts[0] };
    });
    return best.url;
  }

  function loadSource(image, destination) {
    var preview = image.currentSrc || image.getAttribute('src') || image.getAttribute('data-src') || '';
    var source = image.getAttribute('data-rd-hirez-zoom-src') || largestSource(image.getAttribute('data-srcset') || image.getAttribute('srcset')) || preview;
    if (destination.dataset.rdRequestedSource === source) return;
    destination.dataset.rdRequestedSource = source;
    if (preview) destination.src = preview;
    if (!source || source === preview) return;
    var preload = new Image();
    preload.decoding = 'async';
    preload.onload = function () {
      if (destination.isConnected && destination.dataset.rdRequestedSource === source) destination.src = source;
    };
    preload.src = source;
  }

  function ensureLayer(image) {
    var frame = image.closest(FRAME) || image.parentElement;
    if (frame && frame.tagName === 'PICTURE') frame = frame.parentElement;
    if (!frame) return null;
    frame.classList.add('rd-zoom-frame');
    var layer = Array.prototype.find.call(frame.children, function (child) {
      return child.classList.contains('rd-zoom-layer');
    });
    if (!layer) {
      layer = document.createElement('div');
      layer.className = 'rd-zoom-layer';
      layer.setAttribute('aria-hidden', 'true');
      var duplicate = document.createElement('img');
      duplicate.className = 'rd-zoom-image';
      duplicate.alt = '';
      duplicate.draggable = false;
      duplicate.decoding = 'async';
      layer.appendChild(duplicate);
      frame.appendChild(layer);
    }
    var duplicateImage = layer.querySelector('.rd-zoom-image');
    var presentation = window.getComputedStyle(image);
    duplicateImage.style.objectFit = presentation.objectFit;
    duplicateImage.style.objectPosition = presentation.objectPosition;
    loadSource(image, duplicateImage);
    return { frame: frame, layer: layer, duplicate: duplicateImage, image: image };
  }

  function position(point) {
    if (!active || !point) return;
    if (!active.image.isConnected) return deactivate();
    var frameRect = active.frame.getBoundingClientRect();
    var imageRect = active.image.getBoundingClientRect();
    if (!frameRect.width || !frameRect.height || !imageRect.width || !imageRect.height) return deactivate();
    // Convert viewport coordinates into the frame's CSS pixels, including scaled ancestors.
    var ratioX = frameRect.width / active.frame.offsetWidth || 1;
    var ratioY = frameRect.height / active.frame.offsetHeight || 1;
    var width = imageRect.width / ratioX;
    var height = imageRect.height / ratioY;
    var imageX = (imageRect.left - frameRect.left) / ratioX - active.frame.clientLeft;
    var imageY = (imageRect.top - frameRect.top) / ratioY - active.frame.clientTop;
    var x = Math.max(0, Math.min(width, (point.x - imageRect.left) / ratioX));
    var y = Math.max(0, Math.min(height, (point.y - imageRect.top) / ratioY));
    active.duplicate.style.width = width + 'px';
    active.duplicate.style.height = height + 'px';

    if (active.lens) {
      var diameter = Math.min(active.diameter, active.frame.clientWidth, active.frame.clientHeight);
      var half = diameter / 2;
      var centerX = imageX + x;
      var centerY = imageY + y;
      if (active.keepInside) {
        centerX = Math.max(half, Math.min(active.frame.clientWidth - half, centerX));
        centerY = Math.max(half, Math.min(active.frame.clientHeight - half, centerY));
      }
      active.layer.style.width = diameter + 'px';
      active.layer.style.height = diameter + 'px';
      active.layer.style.left = (centerX - half) + 'px';
      active.layer.style.top = (centerY - half) + 'px';
      active.duplicate.style.left = '0px';
      active.duplicate.style.top = '0px';
      active.duplicate.style.transformOrigin = '0 0';
      active.duplicate.style.transform = 'translate3d(' + (half - active.border - x * active.scale) + 'px, ' + (half - active.border - y * active.scale) + 'px, 0) scale(' + active.scale + ')';
    } else {
      active.layer.style.width = '100%';
      active.layer.style.height = '100%';
      active.layer.style.left = '0px';
      active.layer.style.top = '0px';
      active.duplicate.style.left = imageX + 'px';
      active.duplicate.style.top = imageY + 'px';
      active.duplicate.style.transformOrigin = x + 'px ' + y + 'px';
      active.duplicate.style.transform = 'translateZ(0) scale(' + active.scale + ')';
    }
  }

  function queuePosition(event) {
    pendingPoint = { x: event.clientX, y: event.clientY };
    if (animationFrame) return;
    animationFrame = window.requestAnimationFrame(function () {
      animationFrame = 0;
      position(pendingPoint);
    });
  }

  function deactivate() {
    if (animationFrame) window.cancelAnimationFrame(animationFrame);
    animationFrame = 0;
    pendingPoint = null;
    domObserver.disconnect();
    if (!active) return;
    active.frame.classList.remove('is-rd-zoomed', 'is-rd-lens-zoomed', 'is-rd-touch-zoomed');
    active.section.classList.remove('rd-touch-zoom-is-active');
    if (active.swiper && active.swiper.off) active.swiper.off('slideChange', deactivate);
    active = null;
  }

  function activate(info, event, touch) {
    if (!info || !info.image.complete || !info.image.naturalWidth) return;
    if (active && active.image === info.image && active.touch === touch) return queuePosition(event);
    deactivate();
    var state = ensureLayer(info.image);
    if (!state) return;
    var style = window.getComputedStyle(info.section);
    state.section = info.section;
    state.touch = touch;
    state.lens = !touch && info.section.getAttribute('data-rd-product-zoom') !== 'inline';
    state.scale = Math.max(1, numberVariable(style, touch ? '--rd-image-zoom-scale-mobile' : '--rd-image-zoom-scale-desktop', touch ? 2.4 : 3));
    state.diameter = numberVariable(style, '--rd-lens-diameter', 180);
    state.border = numberVariable(style, '--rd-lens-border-width', 2);
    state.keepInside = info.section.getAttribute('data-rd-lens-keep-inside') !== 'false';
    state.layer.classList.toggle('rd-zoom-layer--lens', state.lens);
    active = state;
    position({ x: event.clientX, y: event.clientY });
    state.frame.classList.add('is-rd-zoomed');
    state.frame.classList.toggle('is-rd-lens-zoomed', state.lens);
    state.frame.classList.toggle('is-rd-touch-zoomed', touch);
    state.section.classList.toggle('rd-touch-zoom-is-active', touch);
    var slider = state.image.closest('.swiper, .product-media__slider, .product-media-new__slider');
    state.swiper = slider && slider.swiper;
    if (state.swiper && state.swiper.on) state.swiper.on('slideChange', deactivate);
    domObserver.observe(document.body, { childList: true, subtree: true });
  }

  function stop(event) {
    if (event.cancelable) event.preventDefault();
    event.stopImmediatePropagation();
  }

  document.addEventListener('pointerover', function (event) {
    if (event.pointerType !== 'mouse' && event.pointerType !== 'pen') return;
    var info = context(event.target);
    if (info && desktopEnabled(info.section)) activate(info, event, false);
  }, true);

  document.addEventListener('pointermove', function (event) {
    if (event.pointerType !== 'touch') {
      var info = context(event.target);
      if (info && desktopEnabled(info.section)) activate(info, event, false);
      else if (info && active && active.touch && active.image === info.image && touchEnabled(info.section)) queuePosition(event);
      return;
    }
    if (!gesture || gesture.pointerId !== event.pointerId) return;
    if (Math.abs(event.clientX - gesture.x) > 8 || Math.abs(event.clientY - gesture.y) > 8) gesture.moved = true;
    if (active && active.touch && active.image === gesture.info.image) {
      stop(event);
      queuePosition(event);
    }
  }, { capture: true, passive: false });

  document.addEventListener('pointerout', function (event) {
    if (active && !active.touch && !active.frame.contains(event.relatedTarget)) deactivate();
  }, true);

  document.addEventListener('pointerdown', function (event) {
    var info = context(event.target);
    if (active && !active.frame.contains(event.target)) deactivate();
    if (event.pointerType !== 'touch' || !info || !touchEnabled(info.section)) return;
    // Let an unzoomed swipe reach Swiper. A stationary first tap opens inline zoom.
    if (!event.isPrimary) {
      gesture = null;
      return deactivate();
    }
    gesture = { info: info, pointerId: event.pointerId, x: event.clientX, y: event.clientY, moved: false };
    if (active && active.touch && active.image === info.image) {
      stop(event);
      if (info.image.setPointerCapture) info.image.setPointerCapture(event.pointerId);
      queuePosition(event);
    }
  }, { capture: true, passive: false });

  document.addEventListener('pointerup', function (event) {
    if (!gesture || gesture.pointerId !== event.pointerId) return;
    var tap = gesture;
    gesture = null;
    if (!touchEnabled(tap.info.section)) return;
    if (active && active.touch && active.image === tap.info.image) {
      stop(event);
      if (!tap.moved) deactivate();
    } else if (!tap.moved && tap.info.image.isConnected) {
      stop(event);
      activate(tap.info, event, true);
    }
  }, { capture: true, passive: false });

  document.addEventListener('pointercancel', function (event) {
    if (gesture && gesture.pointerId === event.pointerId) gesture = null;
  }, true);

  // Older Swiper builds also listen for touch events. Stop those only during inline zoom.
  ['touchstart', 'touchmove'].forEach(function (name) {
    document.addEventListener(name, function (event) {
      if (active && active.touch && active.frame.contains(event.target) && event.touches.length === 1) stop(event);
    }, { capture: true, passive: false });
  });

  document.addEventListener('click', function (event) {
    var info = context(event.target);
    if (!info) return;
    if (touchEnabled(info.section)) {
      // Support mouse interaction in the Theme Editor's narrow mobile preview.
      // Actual touch taps have already been handled by the pointerup listener.
      if (event.pointerType === 'mouse' || event.pointerType === 'pen') {
        if (active && active.touch && active.image === info.image) deactivate();
        else activate(info, event, true);
      }
      stop(event);
    } else if (desktopEnabled(info.section) && info.section.classList.contains('product--rd-image-zoom-no-modal')) stop(event);
  }, true);

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape') { gesture = null; deactivate(); }
  });
  document.addEventListener('shopify:section:unload', function (event) {
    if (active && event.target.contains(active.section)) deactivate();
    gesture = null;
  });
  ['shopify:section:load', 'variant:change', 'variant:changed', 'product:variant-change', 'product:zoom:open'].forEach(function (name) {
    document.addEventListener(name, function () { gesture = null; deactivate(); bindEventBus(); });
  });

  function bindEventBus() {
    var bus = window.themeCore && window.themeCore.EventBus;
    if (!bus || typeof bus.listen !== 'function') return;
    if (!eventBusBound) {
      eventBusBound = true;
      ['product:loaded', 'product:zoom:open'].forEach(function (name) {
        bus.listen(name, function () { gesture = null; deactivate(); });
      });
    }
    // Gain uses section-specific events, rather than a generic variant event.
    document.querySelectorAll(SECTION).forEach(function (section) {
      var id = section.getAttribute('data-section-id');
      if (!id) return;
      ['form:' + id + ':change-variant', 'pdp:section-' + id + ':change-variant'].forEach(function (name) {
        if (variantEvents[name]) return;
        variantEvents[name] = true;
        bus.listen(name, function () {
          if (active && active.section.getAttribute('data-section-id') === id) { gesture = null; deactivate(); }
        });
      });
    });
  }
  bindEventBus();
  document.addEventListener('DOMContentLoaded', bindEventBus);
  document.addEventListener('theme:all:loaded', bindEventBus);

  function reset() { gesture = null; deactivate(); }
  window.addEventListener('resize', reset, { passive: true });
  window.addEventListener('blur', reset);
  window.addEventListener('scroll', function () { if (active && !active.touch) deactivate(); }, { passive: true, capture: true });
  if (desktopQuery.addEventListener) desktopQuery.addEventListener('change', reset);
  else if (desktopQuery.addListener) desktopQuery.addListener(reset);
})();
